import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  FileText, 
  Download, 
  Calendar, 
  Search, 
  Clock, 
  Coffee, 
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity,
  DollarSign,
  RefreshCw
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatUtcTime } from '@/utils/dateUtils';

function AdminReportsPage() {
  const { t, isRTL } = useLanguage();
  const [activeTab, setActiveTab] = useState('activity');
  const [fromDate, setFromDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const navigate = useNavigate();
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salaryReport, setSalaryReport] = useState(null);
  const [salaryLoading, setSalaryLoading] = useState(false);
  
  // Fetch staff list on mount
  useEffect(() => {
    fetchStaffList();
  }, []);
  
  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/admin/staff`, {
        withCredentials: true
      });
      setStaffList(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };
  
  const handleGenerateReport = async () => {
    setLoading(true);
    setReportData(null);
    
    try {
      let endpoint = '';
      const params = {
        from_date: fromDate,
        to_date: toDate
      };
      
      if (selectedStaff !== 'all') {
        params.staff_id = selectedStaff;
      }
      
      switch (activeTab) {
        case 'activity':
          endpoint = '/admin/reports/activity';
          break;
        case 'daily':
          endpoint = '/admin/reports/daily-detailed';
          break;
        case 'monthly':
          endpoint = '/admin/reports/monthly-summary';
          break;
        default:
          endpoint = '/admin/reports/activity';
      }
      
      const response = await axios.get(`${API}${endpoint}`, {
        params,
        withCredentials: true
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateSalaryReport = async () => {
    setSalaryLoading(true);
    setSalaryReport(null);
    
    try {
      const params = {
        start_date: fromDate,
        end_date: toDate
      };
      
      if (selectedStaff !== 'all') {
        params.staff_id = selectedStaff;
      }
      
      const response = await axios.get(`${API}/admin/salary-report`, {
        params,
        withCredentials: true
      });
      setSalaryReport(response.data);
    } catch (error) {
      console.error('Failed to generate salary report:', error);
    } finally {
      setSalaryLoading(false);
    }
  };
  
  const handleExportCSV = () => {
    if (!reportData || !reportData.records || reportData.records.length === 0) return;
    
    const csv = convertToCSV(reportData.records);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}-report-${fromDate}-to-${toDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(record =>
      Object.values(record).map(val => `"${val ?? ''}"`).join(',')
    );
    
    return [headers, ...rows].join('\n');
  };
  
  const formatMinutes = (minutes) => {
    if (!minutes) return '0h 0m';
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hrs}h ${mins}m`;
  };
  
    const formatTime = (value) => formatUtcTime(value);

  
  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Present' },
      absent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Absent' },
      late: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Late' },
      not_marked: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Not Marked' }
    };
    const config = statusConfig[status] || statusConfig.not_marked;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };
  
  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Reports & Analytics</h2>
          <p className="text-gray-600 mt-1">Generate detailed attendance and activity reports</p>
        </div>
      </div>
      
      {/* Filters Card */}
      <Card className="border-2 border-purple-100 shadow-md" data-testid="report-filters">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b">
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <Search className="w-5 h-5" />
            Report Filters
          </CardTitle>
          <CardDescription>Select date range and staff to generate reports</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="from-date" className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                From Date
              </Label>
              <Input
                id="from-date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border-gray-300"
                data-testid="from-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="to-date" className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                To Date
              </Label>
              <Input
                id="to-date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border-gray-300"
                data-testid="to-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="staff-filter" className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                Staff Name
              </Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger id="staff-filter" data-testid="staff-filter-select">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.staff_id} value={staff.staff_id}>
                      {staff.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2 lg:col-span-2">
              <Label>&nbsp;</Label>
              <Button
                onClick={handleGenerateReport}
                className="w-full bg-purple-600 hover:bg-purple-700"
                disabled={loading}
                data-testid="generate-report-btn"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    &quot;Generate Report&quot;
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl bg-gray-100 p-1 rounded-xl">
          <TabsTrigger 
            value="activity" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="activity-tab"
          >
            <Activity className="w-4 h-4 mr-2" />
            Activity
          </TabsTrigger>
          <TabsTrigger 
            value="daily" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="daily-tab"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Daily
          </TabsTrigger>
          <TabsTrigger 
            value="monthly" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="monthly-tab"
          >
            <FileText className="w-4 h-4 mr-2" />
            Monthly
          </TabsTrigger>
          <TabsTrigger 
            value="salaries" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="salaries-tab"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Salaries
          </TabsTrigger>
        </TabsList>
        
        {/* Activity Report Tab */}
        <TabsContent value="activity" className="mt-6">
          {reportData && activeTab === 'activity' ? (
            <Card className="shadow-lg" data-testid="activity-report-results">
              <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-indigo-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-indigo-800">
                      <Activity className="w-5 h-5" />
                      Activity Report
                    </CardTitle>
                    <CardDescription>
                      {reportData.total_staff} staff members • {fromDate} to {toDate}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleExportCSV}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="export-activity-csv"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Staff Name</TableHead>
                      <TableHead className="font-semibold text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="w-4 h-4 text-indigo-500" />
                          Total Ready Time
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Coffee className="w-4 h-4 text-orange-500" />
                          Total Break Time
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-center">Days Worked</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                          No data found for the selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportData.records.map((record, index) => (
                        <TableRow key={index} className="hover:bg-gray-50">
                          <TableCell className="font-medium">{record.staff_name}</TableCell>
                          <TableCell className="text-center">
                            <span className="px-3 py-1 rounded-lg bg-indigo-100 text-indigo-700 font-mono font-semibold">
                              {formatMinutes(record.total_ready_minutes)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="px-3 py-1 rounded-lg bg-orange-100 text-orange-700 font-mono font-semibold">
                              {formatMinutes(record.total_break_minutes)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center font-semibold">{record.days_worked}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <Activity className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Activity Report</h3>
                <p className="text-gray-500 mt-2">
                  Shows total ready time and break time per staff member
                </p>
                <p className="text-sm text-gray-400 mt-4">
                  Select date range and click Generate Report
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Daily Report Tab */}
        <TabsContent value="daily" className="mt-6">
          {reportData && activeTab === 'daily' ? (
            <Card className="shadow-lg" data-testid="daily-report-results">
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-blue-800">
                      <Calendar className="w-5 h-5" />
                      Daily Report
                    </CardTitle>
                    <CardDescription>
                      {reportData.total_records} records • {fromDate} to {toDate}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleExportCSV}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="export-daily-csv"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">{t("common.date")}</TableHead>
                        <TableHead className="font-semibold">Staff Name</TableHead>
                        <TableHead className="font-semibold">{t("common.status")}</TableHead>
                        <TableHead className="font-semibold">{t("attendance.check_in")}</TableHead>
                        <TableHead className="font-semibold">{t("attendance.check_out")}</TableHead>
                        <TableHead className="font-semibold text-center">Late By</TableHead>
                        <TableHead className="font-semibold text-center">Breaks</TableHead>
                        <TableHead className="font-semibold text-center">Total Working</TableHead>
                        <TableHead className="font-semibold text-center">Overtime</TableHead>
                        <TableHead className="font-semibold text-center">Sessions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.records.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center text-gray-500 py-8">
                            No data found for the selected period
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.records.map((record, index) => (
                          <TableRow key={index} className="hover:bg-gray-50">
                            <TableCell className="font-medium">
                              {new Date(record.date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </TableCell>
                            <TableCell className="font-medium">{record.staff_name}</TableCell>
                            <TableCell>{getStatusBadge(record.status)}</TableCell>
                            <TableCell className="text-indigo-600 font-medium">
                              {formatTime(record.check_in)}
                            </TableCell>
                            <TableCell className="text-blue-600 font-medium">
                              {formatTime(record.check_out)}
                            </TableCell>
                            <TableCell className="text-center">
                              {record.late_by > 0 ? (
                                <span className="text-amber-600 font-semibold">{record.late_by}m</span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-2">
                                <span className="font-medium">{record.break_count}</span>
                                {record.total_break_minutes > 0 && (
                                  <span className="text-xs text-gray-500">
                                    ({formatMinutes(record.total_break_minutes)})
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {record.total_working_minutes > 0 ? (
                                <span className="font-medium text-indigo-600">
                                  {formatMinutes(record.total_working_minutes)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {record.overtime_minutes > 0 ? (
                                <span className="font-semibold text-purple-600">
                                  {formatMinutes(record.overtime_minutes)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {record.sessions_count > 1 ? (
                                <button
                                  onClick={() => navigate(`/admin/reports/sessions/${record.staff_id}/${record.date}`)}
                                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200 transition-colors cursor-pointer"
                                  data-testid={`view-sessions-${record.staff_id}-${record.date}`}
                                >
                                  {record.sessions_count} sessions →
                                </button>
                              ) : (
                                <span className="text-gray-400">1</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Daily Report</h3>
                <p className="text-gray-500 mt-2">
                  Detailed daily attendance with check-in/out times and breaks
                </p>
                <p className="text-sm text-gray-400 mt-4">
                  Select date range and click Generate Report
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Monthly Report Tab */}
        <TabsContent value="monthly" className="mt-6">
          {reportData && activeTab === 'monthly' ? (
            <Card className="shadow-lg" data-testid="monthly-report-results">
              <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-purple-800">
                      <FileText className="w-5 h-5" />
                      Monthly Summary Report
                    </CardTitle>
                    <CardDescription>
                      {reportData.total_staff} staff members • {fromDate} to {toDate}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleExportCSV}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="export-monthly-csv"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Staff Name</TableHead>
                        <TableHead className="font-semibold text-center">
                          <div className="flex items-center justify-center gap-1">
                            <CheckCircle className="w-4 h-4 text-indigo-500" />
                            Present
                          </div>
                        </TableHead>
                        <TableHead className="font-semibold text-center">
                          <div className="flex items-center justify-center gap-1">
                            <XCircle className="w-4 h-4 text-red-500" />
                            Absent
                          </div>
                        </TableHead>
                        <TableHead className="font-semibold text-center">
                          <div className="flex items-center justify-center gap-1">
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                            Late
                          </div>
                        </TableHead>
                        <TableHead className="font-semibold text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Coffee className="w-4 h-4 text-orange-500" />
                            Breaks
                          </div>
                        </TableHead>
                        <TableHead className="font-semibold text-center">Break Time</TableHead>
                        <TableHead className="font-semibold text-center">Ready Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.records.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                            No data found for the selected period
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.records.map((record, index) => (
                          <TableRow key={index} className="hover:bg-gray-50">
                            <TableCell className="font-medium">{record.staff_name}</TableCell>
                            <TableCell className="text-center">
                              <span className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                                {record.total_present}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 font-semibold">
                                {record.total_absent}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">
                                {record.total_late}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="font-semibold">{record.total_breaks}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="px-3 py-1 rounded-lg bg-orange-100 text-orange-700 font-mono font-semibold">
                                {formatMinutes(record.total_break_minutes)}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="px-3 py-1 rounded-lg bg-indigo-100 text-indigo-700 font-mono font-semibold">
                                {formatMinutes(record.total_ready_minutes)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Monthly Summary Report</h3>
                <p className="text-gray-500 mt-2">
                  Comprehensive summary with attendance counts, breaks, and time analytics
                </p>
                <p className="text-sm text-gray-400 mt-4">
                  Select date range and click Generate Report
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Salaries Tab */}
        <TabsContent value="salaries" className="mt-6">
          <div className="mb-6">
            <Button 
              onClick={handleGenerateSalaryReport} 
              disabled={salaryLoading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {salaryLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Generate Salary Report
                </>
              )}
            </Button>
          </div>
          
          {salaryReport ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Staff Count</p>
                        <p className="text-2xl font-bold">{salaryReport.summary.total_staff}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-indigo-100 rounded-lg">
                        <DollarSign className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Gross</p>
                        <p className="text-2xl font-bold">${salaryReport.summary.total_gross?.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <DollarSign className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Net</p>
                        <p className="text-2xl font-bold">${salaryReport.summary.total_net?.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-red-100 rounded-lg">
                        <DollarSign className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Tax</p>
                        <p className="text-2xl font-bold">${salaryReport.summary.total_tax?.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Period Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Pay Period: {salaryReport.period.start_date} to {salaryReport.period.end_date}
                  </CardTitle>
                  <CardDescription>
                    {salaryReport.period.total_days} total days, {salaryReport.period.working_days} working days
                  </CardDescription>
                </CardHeader>
              </Card>
              
              {/* Staff Salary Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Staff Salary Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {salaryReport.staff_salaries?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <DollarSign className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <p>No staff with salary setup found</p>
                      <p className="text-sm mt-2">Go to Staff page and set up salaries first</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Staff</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Base</TableHead>
                            <TableHead className="text-right">Hours</TableHead>
                            <TableHead className="text-right">Overtime</TableHead>
                            <TableHead className="text-right">Bonus</TableHead>
                            <TableHead className="text-right">Deductions</TableHead>
                            <TableHead className="text-right">Tax</TableHead>
                            <TableHead className="text-right">Net Pay</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {salaryReport.staff_salaries?.map((item) => (
                            <TableRow key={item.staff_id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{item.name}</p>
                                  <p className="text-xs text-gray-500">{item.department || 'No dept'}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {item.salary_type === 'hourly' ? 'Hourly' : 'Monthly'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {item.calculation.currency} {item.calculation.base_pay?.toLocaleString()}
                              </TableCell>
                              <TableCell className="text-right">
                                {item.calculation.working_stats?.total_hours?.toFixed(1)}h
                                <div className="text-xs text-gray-500">
                                  {item.calculation.working_stats?.present_days} days
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                {item.calculation.overtime_pay > 0 ? (
                                  <span className="text-indigo-600">
                                    +{item.calculation.currency} {item.calculation.overtime_pay?.toLocaleString()}
                                  </span>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                {item.calculation.total_bonus > 0 ? (
                                  <span className="text-indigo-600">
                                    +{item.calculation.currency} {item.calculation.total_bonus?.toLocaleString()}
                                  </span>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                {(item.calculation.late_penalty + item.calculation.absence_deduction) > 0 ? (
                                  <span className="text-red-600">
                                    -{item.calculation.currency} {(item.calculation.late_penalty + item.calculation.absence_deduction)?.toLocaleString()}
                                  </span>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                {item.calculation.tax_amount > 0 ? (
                                  <span className="text-orange-600">
                                    -{item.calculation.currency} {item.calculation.tax_amount?.toLocaleString()}
                                  </span>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                {item.calculation.currency} {item.calculation.net_salary?.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <DollarSign className="w-12 h-12 mx-auto text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-900 mt-4">Salary Report</h3>
                <p className="text-gray-500 mt-2">
                  Calculate salaries based on attendance, overtime, deductions, and bonuses
                </p>
                <p className="text-sm text-gray-400 mt-4">
                  Select date range and click &quot;Generate Salary Report&quot;
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Report Type Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Activity className="w-8 h-8 text-indigo-500" />
              <div>
                <h4 className="font-semibold text-gray-900">Activity Report</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Total ready time and break time per staff for the selected period
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Calendar className="w-8 h-8 text-blue-500" />
              <div>
                <h4 className="font-semibold text-gray-900">Daily Report</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Day-by-day attendance with check-in/out times, status, and breaks
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <FileText className="w-8 h-8 text-purple-500" />
              <div>
                <h4 className="font-semibold text-gray-900">Monthly Report</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Summary with present/absent/late counts and total times
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminReportsPage;
